import { useTranslation } from 'react-i18next'
import { ChevronLeft } from 'lucide-react'
import { PublicNavigationBar } from '../components/PublicNavigationBar'

// Import the privacy policy content
const enPrivacyContent = `# Privacy Policy - The Ranch Summer Camp

**Last Updated: March 23, 2025**

## Introduction

At The Ranch, we are committed to protecting your privacy and the privacy of your children. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our booking application.

## Information We Collect

We collect personal information that you voluntarily provide to us when you register on our website, express interest in obtaining information about our services, or otherwise contact us. The personal information we collect may include:

- Parent information (name, email address, phone number, mailing address, social security number, insurance number)
- Child information (name, date of birth, health information, insurance number, allergies, special needs)
- Payment information
- Emergency contact information
- Camp preferences and requirements

## How We Use Your Information

We may use the information we collect from you for various purposes, including to:

- Provide, operate, and maintain our services
- Improve, personalize, and expand our services
- Understand and analyze how you use our services
- Develop new products, services, features, and functionality
- Communicate with you about our services, updates, and other information
- Process payments
- Find and prevent fraud
- Ensure the health and safety of children in our care

## Disclosure of Your Information

We may share information we have collected in certain situations. Your information may be disclosed as follows:

- By Law or to Protect Rights: We may disclose your information when we believe disclosure is necessary to comply with a legal process or protect our rights.
- Third-Party Service Providers: We may share your information with third-party service providers who perform services on our behalf.
- Marketing Communications: With your consent, we may share your information with third parties for marketing purposes.
- Interactions with Other Users: If you interact with other users of our services, those users may see information about you.

## Security of Your Information

We use technical and organizational measures designed to protect the security of all information we process. However, no method of transmission over the Internet or method of electronic storage is 100% secure.

## Children's Privacy

We collect information from or about children only with the consent of their parents or guardians. We comply with the Children's Online Privacy Protection Act (COPPA) and all applicable data protection laws concerning children's data.

## Your Rights Under GDPR

If you are a resident of the European Union, you have certain data protection rights under the GDPR, including:

- The right to access your personal data
- The right to rectify inaccurate personal data
- The right to erasure of your personal data
- The right to restrict processing of your personal data
- The right to data portability
- The right to object to the processing of your personal data
- The right to withdraw consent

## Changes to This Privacy Policy

We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.

## Contact Us

If you have questions or comments about this Privacy Policy, please contact us at:

The Ranch
Corinth, Greece
Email: info@ranch.gr
Phone: +30 2741 0 93801
Website: https://ranch.gr/en`

const elPrivacyContent = `# Πολιτική Απορρήτου - Παιδική Κατασκήνωση The Ranch

**Τελευταία Ενημέρωση: 23 Μαρτίου 2025**

## Εισαγωγή

Στο The Ranch, δεσμευόμαστε να προστατεύουμε το απόρρητό σας και το απόρρητο των παιδιών σας. Αυτή η Πολιτική Απορρήτου εξηγεί πώς συλλέγουμε, χρησιμοποιούμε, αποκαλύπτουμε και προστατεύουμε τις πληροφορίες σας όταν επισκέπτεστε τον ιστότοπό μας και χρησιμοποιείτε την εφαρμογή κρατήσεών μας.

## Πληροφορίες που Συλλέγουμε

Συλλέγουμε προσωπικές πληροφορίες που παρέχετε εθελοντικά όταν εγγράφεστε στον ιστότοπό μας, εκδηλώνετε ενδιαφέρον για πληροφορίες σχετικά με τις υπηρεσίες μας ή επικοινωνείτε μαζί μας. Οι προσωπικές πληροφορίες που συλλέγουμε μπορεί να περιλαμβάνουν:

- Στοιχεία γονέα (όνομα, διεύθυνση email, αριθμό τηλεφώνου, ταχυδρομική διεύθυνση, ΑΦΜ, ΑΜΚΑ)
- Πληροφορίες για το παιδί (όνομα, ημερομηνία γέννησης, πληροφορίες υγείας, αλλεργίες, ειδικές ανάγκες, ΑΜΚΑ)
- Στοιχεία πληρωμής
- Στοιχεία επικοινωνίας έκτακτης ανάγκης
- Προτιμήσεις και απαιτήσεις κατασκήνωσης

## Πώς Χρησιμοποιούμε τις Πληροφορίες σας

Μπορεί να χρησιμοποιήσουμε τις πληροφορίες που συλλέγουμε από εσάς για διάφορους σκοπούς, όπως:

- Παροχή, λειτουργία και συντήρηση των υπηρεσιών μας
- Βελτίωση, εξατομίκευση και επέκταση των υπηρεσιών μας
- Κατανόηση και ανάλυση του τρόπου χρήσης των υπηρεσιών μας
- Ανάπτυξη νέων προϊόντων, υπηρεσιών, χαρακτηριστικών και λειτουργικότητας
- Επικοινωνία μαζί σας σχετικά με τις υπηρεσίες μας, ενημερώσεις και άλλες πληροφορίες
- Επεξεργασία πληρωμών
- Εντοπισμός και πρόληψη απάτης
- Διασφάλιση της υγείας και της ασφάλειας των παιδιών που φροντίζουμε

## Αποκάλυψη των Πληροφοριών σας

Μπορεί να μοιραστούμε πληροφορίες που έχουμε συλλέξει σε ορισμένες καταστάσεις. Οι πληροφορίες σας μπορεί να αποκαλυφθούν ως εξής:

- Από το Νόμο ή για την Προστασία Δικαιωμάτων: Μπορεί να αποκαλύψουμε τις πληροφορίες σας όταν πιστεύουμε ότι η αποκάλυψη είναι απαραίτητη για συμμόρφωση με μια νομική διαδικασία ή για την προστασία των δικαιωμάτων μας.
- Τρίτοι Πάροχοι Υπηρεσιών: Μπορεί να μοιραστούμε τις πληροφορίες σας με τρίτους παρόχους υπηρεσιών που εκτελούν υπηρεσίες για λογαριασμό μας.
- Επικοινωνίες Μάρκετινγκ: Με τη συγκατάθεσή σας, μπορεί να μοιραστούμε τις πληροφορίες σας με τρίτους για σκοπούς μάρκετινγκ.
- Αλληλεπιδράσεις με Άλλους Χρήστες: Εάν αλληλεπιδράτε με άλλους χρήστες των υπηρεσιών μας, αυτοί οι χρήστες μπορεί να δουν πληροφορίες σχετικά με εσάς.

## Ασφάλεια των Πληροφοριών σας

Χρησιμοποιούμε τεχνικά και οργανωτικά μέτρα σχεδιασμένα για την προστασία της ασφάλειας όλων των πληροφοριών που επεξεργαζόμαστε. Ωστόσο, καμία μέθοδος μετάδοσης μέσω του Διαδικτύου ή μέθοδος ηλεκτρονικής αποθήκευσης δεν είναι 100% ασφαλής.

## Απόρρητο Παιδιών

Συλλέγουμε πληροφορίες από ή σχετικά με παιδιά μόνο με τη συγκατάθεση των γονέων ή κηδεμόνων τους. Συμμορφωνόμαστε με το Νόμο Προστασίας Διαδικτυακού Απορρήτου Παιδιών (COPPA) και όλους τους ισχύοντες νόμους προστασίας δεδομένων σχετικά με τα δεδομένα παιδιών.

## Τα Δικαιώματά σας βάσει του GDPR

Εάν είστε κάτοικος της Ευρωπαϊκής Ένωσης, έχετε ορισμένα δικαιώματα προστασίας δεδομένων βάσει του GDPR, συμπεριλαμβανομένων:

- Το δικαίωμα πρόσβασης στα προσωπικά σας δεδομένα
- Το δικαίωμα διόρθωσης ανακριβών προσωπικών δεδομένων
- Το δικαίωμα διαγραφής των προσωπικών σας δεδομένων
- Το δικαίωμα περιορισμού της επεξεργασίας των προσωπικών σας δεδομένων
- Το δικαίωμα φορητότητας δεδομένων
- Το δικαίωμα αντίρρησης στην επεξεργασία των προσωπικών σας δεδομένων
- Το δικαίωμα ανάκλησης της συγκατάθεσης

## Αλλαγές σε Αυτήν την Πολιτική Απορρήτου

Μπορεί να ενημερώσουμε την Πολιτική Απορρήτου μας από καιρό σε καιρό. Θα σας ενημερώσουμε για τυχόν αλλαγές αναρτώντας τη νέα Πολιτική Απορρήτου σε αυτήν τη σελίδα και ενημερώνοντας την ημερομηνία "Τελευταία Ενημέρωση".

## Επικοινωνήστε Μαζί μας

Εάν έχετε ερωτήσεις ή σχόλια σχετικά με αυτήν την Πολιτική Απορρήτου, παρακαλούμε επικοινωνήστε μαζί μας στο:

The Ranch
Κόρινθος, Ελλάδα
Email: info@ranch.gr
Τηλέφωνο: +30 2741 0 93801
Ιστότοπος: https://ranch.gr/el`

export function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation(['common'])
  const currentLanguage = i18n.language

  // Choose content based on current language
  const privacyContent = currentLanguage === 'el' ? elPrivacyContent : enPrivacyContent

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicNavigationBar />
      <main className="flex-grow max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8 w-full">
        <a href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
          <ChevronLeft className="h-4 w-4" />
          {t('backToHome')}
        </a>
        <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 sm:p-8">
          <h1 className="text-xl font-semibold text-foreground mb-6">{t('legal.privacyPolicy')}</h1>
          <div className="max-w-none text-sm leading-relaxed text-muted-foreground">
            <div dangerouslySetInnerHTML={{ 
              __html: privacyContent.split('\n').map(line => {
                if (line.startsWith('# ')) return `<h1 class="text-lg font-semibold text-foreground mt-8 mb-3">${line.replace('# ', '')}</h1>`;
                if (line.startsWith('## ')) return `<h2 class="text-base font-semibold text-foreground mt-6 mb-2">${line.replace('## ', '')}</h2>`;
                if (line.startsWith('### ')) return `<h3 class="text-sm font-semibold text-foreground mt-4 mb-1.5">${line.replace('### ', '')}</h3>`;
                if (line.startsWith('- ')) return `<li class="ml-5 mb-1.5 list-disc">${line.replace('- ', '')}</li>`;
                if (line.trim() === '') return '<div class="h-2"></div>';
                if (line.includes('**')) return `<p class="mb-3">${line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-medium text-foreground">$1</strong>')}</p>`;
                return `<p class="mb-3">${line}</p>`;
              }).join('')
            }} />
          </div>
        </div>
      </main>
    </div>
  )
} 